import { useRef } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import Toggle from "@/app/components/ui/Toggles";
import {
  cardClassName,
  fieldLabelClassName,
  getRibbonTextLength,
  RibbonTextSuggestion,
  textInputClassName,
} from "../../HomeTileConfig/interface";
import { RibbonTextSuggestions } from "../../HomeTileConfig/controls";
import { PersonaLogicField } from "../../HomeTileConfig/HomeTileOnboard/PersonaLogicField";
import { IClinicTagFormFields, TAG_TEXT_MAX_LENGTH, validateTagText } from "../interface";

const TagTextField = () => {
  const { control, getValues, register, setValue } = useFormContext<IClinicTagFormFields>();
  const value = useWatch({ control, name: "chip_txt" }) ?? "";
  const ribbon = useWatch({ control, name: "ribbon" });
  const { errors } = useFormState({ control, name: "chip_txt" });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const length = getRibbonTextLength(value);

  const { ref, ...registration } = register("chip_txt", { validate: validateTagText });

  const insertEmoji = (emoji: string) => {
    const currentValue = getValues("chip_txt") ?? "";
    const input = inputRef.current;
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    setValue("chip_txt", `${currentValue.slice(0, start)}${emoji}${currentValue.slice(end)}`, {
      shouldValidate: true,
      shouldDirty: true,
    });
    requestAnimationFrame(() => {
      const caret = start + emoji.length;
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const applySuggestion = ({ text, bg_color, text_color, border_color }: RibbonTextSuggestion) => {
    const currentText = getValues("chip_txt") ?? "";
    const nextText = currentText.trim() ? currentText : text;
    if (nextText !== currentText) setValue("chip_txt", nextText, { shouldValidate: true, shouldDirty: true });
    setValue("ribbon", { bg_color, text_color, border_color }, { shouldDirty: true });
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextText.length, nextText.length);
    });
  };

  return (
    <div>
      <label className={fieldLabelClassName} htmlFor="tag-text">
        Tag Text
      </label>
      <input
        {...registration}
        ref={(element) => {
          ref(element);
          inputRef.current = element;
        }}
        id="tag-text"
        type="text"
        placeholder="e.g. Free SPA"
        className={`${textInputClassName} ${errors.chip_txt ? "border-rose-400" : ""}`}
      />
      <div className="mt-1.5 flex items-start justify-between gap-2">
        <p className="text-[11px] text-rose-600">{errors.chip_txt?.message ?? ""}</p>
        <span
          className={`shrink-0 text-[11px] tabular-nums ${
            length > TAG_TEXT_MAX_LENGTH ? "font-semibold text-rose-600" : "text-slate-400"
          }`}
        >
          {length}/{TAG_TEXT_MAX_LENGTH}
        </span>
      </div>
      <RibbonTextSuggestions
        disabled={length >= TAG_TEXT_MAX_LENGTH}
        onSelect={applySuggestion}
        onInsertEmoji={insertEmoji}
        selectedStyle={ribbon}
      />
    </div>
  );
};

export const TagRuleCard = ({
  personas,
  isPersonaListLoading,
  validatePersona,
}: {
  personas: string[];
  isPersonaListLoading: boolean;
  validatePersona: (value: string) => true | string;
}) => {
  const { control, setValue, trigger } = useFormContext<IClinicTagFormFields>();
  const status = useWatch({ control, name: "status" });

  const setIsActive = (nextIsActive: boolean) => {
    setValue("status", nextIsActive ? "ACTIVE" : "INACTIVE", { shouldDirty: true });
    void trigger("persona_combination");
  };

  return (
    <div className={cardClassName}>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
        <span className="text-sm font-semibold text-gray-900">Tag</span>
        <div className="ml-auto flex items-center gap-2.5">
          <span className="text-xs font-medium text-slate-600">Status</span>
          <Toggle key={`rule-status-${status}`} checked={status === "ACTIVE"} onToggle={setIsActive} />
        </div>
      </div>

      <div className="px-5 py-4">
        <PersonaLogicField personas={personas} isPersonaListLoading={isPersonaListLoading} validate={validatePersona} />
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <TagTextField />
        </div>
      </div>
    </div>
  );
};
